import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import isAfter from 'date-fns/isAfter'
import { isEmpty } from 'lodash'

import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '@10xLabs/react-web/components/Table'
import { FormattedRoute } from '@10xLabs/react-web/components/FormattedRoute'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { FormattedTime } from '@10xLabs/react-web/components/FormattedTime'
import { Button } from 'components/v2/Button'
import { IBookingTripsDetailsProps, ITripRowProps } from 'features/Bookings/components/BookingTripsDetails/types'
import { ReactElement } from 'react'

const StyledTd = styled(Td)<TSFixMe>`
  vertical-align: middle;
`

const StyledButton = styled(Button)<TSFixMe>`
  padding: 10px 25px;
`

const Subtitle = styled.div<TSFixMe>`
  font-weight: 500;
`

const TripRow = ({
  trip,
  tickets,
  isMoveBooking,
  onEditTrip,
  isModifiedTrip,
  isSomeTripModificationStarted
}: ITripRowProps): ReactElement => {
  const intl = useIntl()

  const ticketCheckedIn = tickets.find((ticket) => ticket.isCheckedIn)

  const now = new Date()
  const isTripEditable =
    !trip.segment.departureAt || isAfter(new Date(trip.segment.departureAt), now) || isEmpty(ticketCheckedIn)
  const isOtherTripModified = isSomeTripModificationStarted && !isModifiedTrip
  const isTripEnabled = isTripEditable && !isOtherTripModified
  const originTimezone = trip.segment.origin.city.timezone

  return (
    <Tr>
      <StyledTd>
        <FormattedRoute {...trip.segment} />
      </StyledTd>
      <StyledTd>
        {trip.segment.departureAt ? (
          <FormattedDate
            intl={intl}
            value={trip.segment.departureAt}
            month="2-digit"
            day="2-digit"
            year="numeric"
            timeZone={originTimezone || undefined}
          />
        ) : (
          trip.departureDate
        )}
      </StyledTd>
      <StyledTd>
        {trip.segment.departureAt ? (
          <FormattedTimeByDate intl={intl} date={trip.segment.departureAt} timeZone={originTimezone || undefined} />
        ) : (
          <FormattedTime intl={intl} time={trip.departureTime} noIcon />
        )}
      </StyledTd>
      <StyledTd>
        <StyledButton
          onClick={(): TSFixMe => {
            if (isTripEnabled) {
              onEditTrip(trip)
            }
          }}
          type="button"
          kind="primary"
          small
          disabled={!isTripEnabled}
        >
          {isMoveBooking ? (
            <FormattedMessage id="dictionary.move" defaultMessage="Move" />
          ) : (
            <FormattedMessage id="dictionary.edit" defaultMessage="Edit" />
          )}
        </StyledButton>
      </StyledTd>
    </Tr>
  )
}

export const BookingTripsDetails = ({
  departureTrips = [],
  returnTrips = [],
  tickets = [],
  isMoveBooking = false,
  onEditTrip,
  tripChangeProposalId,
  modifiedTripId
}: IBookingTripsDetailsProps): ReactElement => {
  const departureTickets = tickets.filter(({ isReturn }) => !isReturn)
  const returnTickets = tickets.filter(({ isReturn }) => isReturn)
  const isSomeTripModificationStarted = !!tripChangeProposalId

  return (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            <Th>
              <FormattedMessage id="bookings.form.trips.segment.label" defaultMessage="Origin / Destination" />
            </Th>
            <Th>
              <FormattedMessage id="bookings.form.trips.date.label" defaultMessage="Departure date" />
            </Th>
            <Th>
              <FormattedMessage id="bookings.form.trips.hour.label" defaultMessage="Departure hour" />
            </Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {departureTickets.length > 0 && (
            <>
              {returnTickets.length > 0 && (
                <tr>
                  <Td colSpan="4">
                    <Subtitle>
                      <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
                    </Subtitle>
                  </Td>
                </tr>
              )}
              {departureTrips.map((trip, index): ReactElement => {
                return (
                  <TripRow
                    key={index}
                    trip={trip}
                    tickets={departureTickets.filter((ticket) => ticket.trip?.id === trip.id)}
                    onEditTrip={onEditTrip}
                    isMoveBooking={isMoveBooking}
                    isModifiedTrip={modifiedTripId === trip.id}
                    isSomeTripModificationStarted={isSomeTripModificationStarted}
                  />
                )
              })}
            </>
          )}
          {returnTickets.length > 0 && (
            <>
              {departureTickets.length > 0 && (
                <tr>
                  <Td colSpan="4">
                    <Subtitle>
                      <FormattedMessage id="dictionary.return" defaultMessage="Return" />
                    </Subtitle>
                  </Td>
                </tr>
              )}
              {returnTrips.map((trip, index): ReactElement => {
                return (
                  <TripRow
                    key={index}
                    trip={trip}
                    tickets={returnTickets.filter((ticket) => ticket.trip?.id === trip.id)}
                    onEditTrip={onEditTrip}
                    isMoveBooking={isMoveBooking}
                    isModifiedTrip={modifiedTripId === trip.id}
                    isSomeTripModificationStarted={isSomeTripModificationStarted}
                  />
                )
              })}
            </>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
